import { useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft, User as UserIcon, Sun, Building2, Users, UserCheck,
  Package, Ticket, MapPinned,
} from 'lucide-react';
import {
  useCoach, useCoachPackages, useMySessionCredits, useCourtsAtClub,
  useCoachEligibleClubs, useClubHours, useCoachHours,
} from '../../hooks/useAPI';

interface BookCoachScreenProps {
  coachId: string;
  onNavigate: (screen: any, params?: any) => void;
  onBook: (payload: {
    coachId: string;
    lessonType: 'private' | 'semi_private' | 'group';
    environment: 'indoor' | 'outdoor';
    sessionMinutes: number;
    scheduledAt: string;
    participants: number;
    sessionCreditId?: string;
    courtId?: string;
    clubId?: string;
  }) => void;
  onBuyPackage: (packageId: string) => void;
}

type LessonType = 'private' | 'semi_private' | 'group';
type Environment = 'indoor' | 'outdoor';

const LESSON_LABELS: Record<LessonType, { label: string; icon: any }> = {
  private:      { label: 'Private',      icon: UserIcon },
  semi_private: { label: 'Semi-private', icon: UserCheck },
  group:        { label: 'Group',        icon: Users },
};

/**
 * Dedicated booking flow for a coach. Lifted out of CoachDetailScreen
 * in slice 2A so the profile reads as info-only and the booking
 * funnel has a clean, focused screen — easier for the customer to
 * stay focused on the booking decision, easier for us to redesign
 * the booking UX without touching profile rendering.
 *
 * Behaviour is intentionally identical to the previous in-page
 * booking section: lift-and-shift first, redesign in slice 2B
 * (lesson-type-first, time-of-day grouping, sticky CTA spec).
 */
export default function BookCoachScreen({
  coachId, onNavigate, onBook, onBuyPackage,
}: BookCoachScreenProps) {
  const { data: coach } = useCoach(coachId);
  const { data: packages } = useCoachPackages(coachId);
  const { data: credits } = useMySessionCredits();
  const c = coach as any;
  const pkgs = (packages as any[]) || [];
  const myCredits = (credits as any[]) || [];

  const [bundleCourt, setBundleCourt] = useState(false);
  const [selectedCourtId, setSelectedCourtId] = useState<string | null>(null);

  const { data: eligibleClubs } = useCoachEligibleClubs(c?.id);
  const clubs = (eligibleClubs as any[]) || [];
  const isFreelance = c?.type === 'freelance';

  const [selectedClubId, setSelectedClubId] = useState<string | null>(null);
  const activeClubId = isFreelance
    ? selectedClubId
    : (c?.home_club_id ?? null);
  const activeClub = clubs.find((k: any) => k.id === activeClubId)
    ?? (c?.clubs && c.clubs.id === activeClubId ? c.clubs : null);

  // Auto-pick the first eligible club for freelance coaches.
  useEffect(() => {
    if (isFreelance && !selectedClubId && clubs.length > 0) {
      setSelectedClubId(clubs[0].id);
    }
  }, [isFreelance, clubs, selectedClubId]);

  const { data: courtsAtClub } = useCourtsAtClub(activeClubId ?? undefined, undefined);
  const clubCourts = (courtsAtClub as any[]) || [];

  const { data: clubHoursData } = useClubHours(activeClubId ?? undefined);
  const { data: coachHoursData } = useCoachHours(c?.id);
  const clubHours  = (clubHoursData  as any[]) || [];
  const coachHours = (coachHoursData as any[]) || [];

  const rates = (c?.coach_lesson_types ?? []).filter((r: any) => r.is_active) as any[];

  const availableTypes = useMemo(() => {
    const set = new Set(rates.map((r) => r.lesson_type));
    return (['private', 'semi_private', 'group'] as LessonType[]).filter((t) => set.has(t));
  }, [rates]);

  const [lessonType, setLessonType] = useState<LessonType>('private');
  const [environment, setEnvironment] = useState<Environment>('outdoor');

  useEffect(() => {
    if (availableTypes.length && !availableTypes.includes(lessonType)) {
      setLessonType(availableTypes[0]);
    }
  }, [availableTypes, lessonType]);

  const availableEnvs = useMemo(() => {
    const forType = rates.filter((r) => r.lesson_type === lessonType);
    return (['outdoor', 'indoor'] as Environment[]).filter((e) => forType.some((r) => r.environment === e));
  }, [rates, lessonType]);

  useEffect(() => {
    if (availableEnvs.length && !availableEnvs.includes(environment)) {
      setEnvironment(availableEnvs[0]);
    }
  }, [availableEnvs, environment]);

  const activeRate = rates.find(
    (r) => r.lesson_type === lessonType && r.environment === environment && r.is_active,
  );

  const dates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => {
      const d = new Date(); d.setDate(d.getDate() + i + 1);
      return d.toISOString().split('T')[0];
    }),
    [],
  );
  // Bootstrap from location.state when the caller passes prefillLessonType
  // / prefillEnvironment / prefillDate (e.g. the profile rate-card grid
  // pushed us in with a chosen combo). Otherwise default to the first
  // bookable option.
  const [selectedDate, setSelectedDate] = useState(dates[0]);

  // Slot generator — every 30 min, gated by both club hours and coach
  // hours. An empty list on either side counts as unrestricted.
  const slots = useMemo(() => {
    const dow = new Date(`${selectedDate}T12:00:00`).getDay();
    const sessionMin = (activeRate as any)?.session_minutes ?? 60;
    const toMin = (t: string) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    const clubBoundsForDay = clubHours
      .filter((r) => r.day_of_week === dow)
      .map((r) => [toMin(r.open_time), toMin(r.close_time)] as [number, number]);
    const coachBoundsForDay = coachHours
      .filter((r) => r.day_of_week === dow)
      .map((r) => [toMin(r.start_time), toMin(r.end_time)] as [number, number]);
    const withinAny = (bounds: [number, number][], start: number, end: number) =>
      bounds.length === 0 || bounds.some(([s, e]) => s <= start && e >= end);

    const out: string[] = [];
    for (let h = 6; h < 24; h++) {
      for (const m of [0, 30]) {
        const start = h * 60 + m;
        const end = start + sessionMin;
        if (end > 24 * 60) continue;
        if (!withinAny(clubBoundsForDay, start, end)) continue;
        if (!withinAny(coachBoundsForDay, start, end)) continue;
        out.push(`${String(h).padStart(2, '0')}:${m === 0 ? '00' : '30'}`);
      }
    }
    return out;
  }, [selectedDate, clubHours, coachHours, activeRate]);

  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [participants, setParticipants] = useState(1);
  const [useCredit, setUseCredit] = useState(false);

  if (!c) {
    return (
      <div className="w-full h-screen bg-background flex items-center justify-center text-sm text-muted-foreground">
        Loading coach…
      </div>
    );
  }

  const sessionMinutes = activeRate?.session_minutes ?? 60;
  const total = activeRate ? Number(activeRate.per_person_price) * participants : 0;
  const maxP = activeRate?.max_participants
    ?? (lessonType === 'group' ? 6 : lessonType === 'semi_private' ? 2 : 1);

  const matchingCredit = myCredits.find((cr: any) => {
    const p = cr.session_packages;
    return p?.coach_id === c?.id
      && p?.lesson_type === lessonType
      && p?.environment === environment
      && p?.session_minutes === sessionMinutes
      && cr.sessions_remaining > 0;
  });

  const availableCourts = clubCourts.filter((ct) =>
    environment === 'indoor' ? ct.is_indoor : !ct.is_indoor,
  );
  const courtHourlyEstimate = availableCourts.find((ct) => ct.id === selectedCourtId);
  const courtCostEstimate = courtHourlyEstimate
    ? Number(courtHourlyEstimate.hourly_rate_offpeak) * (sessionMinutes ?? 60) / 60
    : 0;

  const canBundle = !!activeClubId && availableCourts.length > 0 && !useCredit;
  const bundling = bundleCourt && canBundle;
  const canBook = !!activeRate && !!selectedSlot
    && (!bundling || !!selectedCourtId)
    && (!isFreelance || !!activeClubId);

  const submit = () => {
    if (!canBook) return;
    const scheduledAt = new Date(`${selectedDate}T${selectedSlot}:00`).toISOString();
    onBook({
      coachId: c.id,
      lessonType,
      environment,
      sessionMinutes,
      scheduledAt,
      participants,
      sessionCreditId: useCredit && matchingCredit ? matchingCredit.id : undefined,
      courtId: bundling ? (selectedCourtId ?? undefined) : undefined,
      clubId: activeClubId ?? undefined,
    });
  };

  return (
    <div className="w-full min-h-screen bg-background pb-32">
      {/* Compact header — back button + coach name. The full hero
          stays on the profile screen; this surface is task-focused. */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => onNavigate('coach', { id: coachId })}
          className="w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center"
          aria-label="Back to coach profile"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-medium leading-tight truncate">Book {c.display_name}</h1>
          <div className="text-xs text-muted-foreground truncate">
            {c.clubs?.name ?? (c.type === 'freelance' ? 'Freelance coach' : 'Staff coach')}
          </div>
        </div>
      </div>

      <div className="px-6 pt-5 space-y-5">
        <div>
          <h3 className="text-sm font-medium mb-2">Lesson type</h3>
          <div className="grid grid-cols-3 gap-2">
            {availableTypes.map((t) => {
              const sel = lessonType === t;
              const { label, icon: Icon } = LESSON_LABELS[t];
              return (
                <button
                  key={t}
                  onClick={() => setLessonType(t)}
                  className={`h-16 rounded-xl flex flex-col items-center justify-center gap-1 transition-all ${
                    sel ? 'bg-[#1A6B4F] text-white' : 'bg-card border border-border'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-xs">{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {availableEnvs.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-2">Where</h3>
            <div className="grid grid-cols-2 gap-2">
              {availableEnvs.map((e) => {
                const sel = environment === e;
                const Icon = e === 'indoor' ? Building2 : Sun;
                return (
                  <button
                    key={e}
                    onClick={() => setEnvironment(e)}
                    className={`h-12 rounded-xl flex items-center justify-center gap-2 transition-all capitalize ${
                      sel ? 'bg-[#1A6B4F] text-white' : 'bg-card border border-border'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {e}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Club picker — freelance only. */}
        {isFreelance && (
          <div>
            <h3 className="text-sm font-medium mb-2">Club</h3>
            {clubs.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-4 text-sm text-muted-foreground">
                This coach isn't affiliated with any club yet — they'll be bookable once
                a club approves them.
              </div>
            ) : clubs.length === 1 ? (
              <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#D4F04A]/20 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#1A6B4F]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{clubs[0].name}</div>
                  {clubs[0].address && (
                    <div className="text-xs text-muted-foreground truncate">{clubs[0].address}</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {clubs.map((k: any) => {
                  const sel = selectedClubId === k.id;
                  return (
                    <button
                      key={k.id}
                      onClick={() => { setSelectedClubId(k.id); setSelectedCourtId(null); }}
                      className={`w-full text-left p-3 rounded-xl border-2 flex items-center gap-3 transition-colors ${
                        sel ? 'border-[#1A6B4F] bg-[#D4F04A]/10' : 'border-border bg-card hover:border-[#1A6B4F]/40'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        sel ? 'bg-[#1A6B4F] text-white' : 'bg-[#D4F04A]/20 text-[#1A6B4F]'
                      }`}>
                        <Building2 className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{k.name}</div>
                        {k.address && (
                          <div className="text-xs text-muted-foreground truncate">{k.address}</div>
                        )}
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        sel ? 'bg-[#1A6B4F] border-[#1A6B4F]' : 'border-muted-foreground/40'
                      }`}>
                        {sel && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {lessonType !== 'private' && (
          <div>
            <h3 className="text-sm font-medium mb-2">Participants</h3>
            <div className="flex gap-2">
              {Array.from({ length: Math.max(1, maxP) }, (_, i) => i + 1).map((n) => {
                const sel = participants === n;
                return (
                  <button
                    key={n}
                    onClick={() => setParticipants(n)}
                    className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                      sel ? 'bg-[#1A6B4F] text-white' : 'bg-card border border-border'
                    }`}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div>
          <h3 className="text-sm font-medium mb-2">Date</h3>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {dates.map((d) => {
              const date = new Date(d);
              const sel = d === selectedDate;
              return (
                <button
                  key={d}
                  onClick={() => { setSelectedDate(d); setSelectedSlot(null); }}
                  className={`flex-shrink-0 w-16 h-20 rounded-2xl flex flex-col items-center justify-center transition-all ${
                    sel ? 'bg-[#1A6B4F] text-white' : 'bg-card border border-border'
                  }`}
                >
                  <div className={`text-[11px] uppercase ${sel ? 'text-white/80' : 'text-muted-foreground'}`}>
                    {date.toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                  <div className="text-xl font-medium leading-none mt-0.5">{date.getDate()}</div>
                  <div className={`text-[11px] mt-1 ${sel ? 'text-white/80' : 'text-muted-foreground'}`}>
                    {date.toLocaleDateString('en-US', { month: 'short' })}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {matchingCredit && (
          <button
            onClick={() => setUseCredit(!useCredit)}
            className={`w-full text-left rounded-2xl p-4 flex items-center gap-3 transition-colors border-2 ${
              useCredit
                ? 'bg-[#D4F04A]/15 border-[#1A6B4F]'
                : 'bg-[#D4F04A]/10 border-[#1A6B4F]/20 hover:border-[#1A6B4F]/40'
            }`}
          >
            <div className="w-10 h-10 rounded-xl bg-[#1A6B4F] flex items-center justify-center flex-shrink-0">
              <Ticket className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#1A6B4F]">
                {useCredit ? 'Using session credit' : 'Use a session credit'}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {matchingCredit.sessions_remaining} / {matchingCredit.sessions_purchased} lessons left · expires{' '}
                {new Date(matchingCredit.expires_at).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric',
                })}
              </div>
            </div>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              useCredit ? 'bg-[#1A6B4F] border-[#1A6B4F]' : 'border-[#1A6B4F]/40'
            }`}>
              {useCredit && <div className="w-2 h-2 rounded-full bg-white" />}
            </div>
          </button>
        )}

        {!useCredit && pkgs.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
              <Package className="w-4 h-4 text-[#1A6B4F]" />
              Save with a package
            </h3>
            <div className="space-y-2">
              {pkgs
                .filter((p) =>
                  p.lesson_type === lessonType
                  && p.environment === environment
                  && p.session_minutes === sessionMinutes,
                )
                .map((p) => {
                  const perSession = Number(p.price) / p.sessions_included;
                  const drop = activeRate
                    ? Math.round((1 - perSession / Number(activeRate.per_person_price)) * 100)
                    : 0;
                  return (
                    <button
                      key={p.id}
                      onClick={() => onBuyPackage(p.id)}
                      className="w-full text-left bg-card border border-border hover:border-[#1A6B4F]/40 rounded-2xl p-4 flex items-center justify-between transition-colors"
                    >
                      <div>
                        <div className="text-sm font-medium">{p.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {p.sessions_included} sessions ·{' '}
                          AED {perSession.toFixed(0)}/session
                          {drop > 0 && (
                            <span className="text-[#1A6B4F] font-medium ml-1">
                              save {drop}%
                            </span>
                          )}
                          {' · '}valid {p.validity_weeks}w
                        </div>
                      </div>
                      <div className="text-lg text-[#1A6B4F] font-medium">
                        AED {Number(p.price).toFixed(0)}
                      </div>
                    </button>
                  );
                })}
            </div>
          </div>
        )}

        {canBundle && (
          <div>
            <button
              onClick={() => { setBundleCourt(!bundleCourt); if (bundleCourt) setSelectedCourtId(null); }}
              className={`w-full text-left rounded-2xl p-4 flex items-center gap-3 transition-colors border-2 ${
                bundling
                  ? 'bg-[#D4F04A]/15 border-[#1A6B4F]'
                  : 'bg-card border-border hover:border-[#1A6B4F]/40'
              }`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                bundling ? 'bg-[#1A6B4F] text-white' : 'bg-secondary text-[#1A6B4F]'
              }`}>
                <MapPinned className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">
                  {bundling ? 'Court included' : `Book a court at ${activeClub?.name ?? 'the club'} too`}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  One payment — you and the coach meet on court, no separate booking.
                </div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                bundling ? 'bg-[#1A6B4F] border-[#1A6B4F]' : 'border-muted-foreground/40'
              }`}>
                {bundling && <div className="w-2 h-2 rounded-full bg-white" />}
              </div>
            </button>

            {bundling && (
              <div className="mt-2 grid gap-2">
                {availableCourts.length === 0 ? (
                  <div className="text-xs text-muted-foreground bg-card border border-border rounded-xl p-3">
                    No {environment} courts at this club — pick a different environment.
                  </div>
                ) : (
                  availableCourts.map((ct) => {
                    const sel = selectedCourtId === ct.id;
                    const rateHour = Number(ct.hourly_rate_offpeak);
                    return (
                      <button
                        key={ct.id}
                        onClick={() => setSelectedCourtId(ct.id)}
                        className={`p-3 rounded-xl border-2 text-left flex items-center justify-between transition-colors ${
                          sel ? 'border-[#1A6B4F] bg-[#D4F04A]/10' : 'border-border bg-card hover:border-[#1A6B4F]/40'
                        }`}
                      >
                        <div>
                          <div className="text-sm font-medium">{ct.name}</div>
                          <div className="text-xs text-muted-foreground capitalize">
                            {ct.surface} · {ct.is_indoor ? 'Indoor' : 'Outdoor'}{ct.has_lights ? ' · Lights' : ''}
                          </div>
                        </div>
                        <div className="text-sm text-[#1A6B4F]">AED {rateHour.toFixed(0)}/hr</div>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}

        <div>
          <h3 className="text-sm font-medium mb-2">Start time</h3>
          <div className="grid grid-cols-4 gap-2">
            {slots.map((t) => {
              const sel = selectedSlot === t;
              return (
                <button
                  key={t}
                  onClick={() => setSelectedSlot(t)}
                  className={`h-11 rounded-xl text-sm transition-all ${
                    sel ? 'bg-[#1A6B4F] text-white shadow-sm' : 'bg-card border border-border hover:border-[#1A6B4F]/40'
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="fixed bottom-20 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 mr-auto">
            <div className="text-[11px] text-muted-foreground leading-none">
              {activeRate ? `${sessionMinutes}-min ${LESSON_LABELS[lessonType].label.toLowerCase()} lesson` : 'Pick a lesson type'}
            </div>
            <div className="text-base text-[#1A6B4F] font-medium leading-tight">
              {useCredit && matchingCredit
                ? 'Free · 1 session credit'
                : activeRate
                  ? `${activeRate.currency} ${(total + (bundling ? courtCostEstimate : 0)).toFixed(0)}`
                  : '—'}
            </div>
            {bundling && courtHourlyEstimate && (
              <div className="text-[11px] text-muted-foreground leading-none mt-0.5">
                incl. court · {activeRate?.currency} {courtCostEstimate.toFixed(0)}
              </div>
            )}
          </div>
          <button
            disabled={!canBook}
            onClick={submit}
            className="h-12 px-5 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm font-medium disabled:opacity-40"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
